import { filter, hasProp, isPlainObject, map, pipe } from 'remeda'

type EditorValueParams = {
  readonly value: unknown
  readonly data: Readonly<Record<string, unknown>> | undefined
  readonly column: {
    readonly getColId: () => string
  }
}

export type EditorValueCell =
  | (Readonly<Record<string, unknown>> & { readonly value: unknown })
  | (Readonly<Record<string, unknown>> & { readonly _serverValue: unknown })

export type RawDisplayCell = Readonly<Record<string, unknown>> & {
  readonly value: unknown
  readonly _serverValue: unknown
}

export type ParsedEditorValue = Readonly<{
  __facadeGridDisplayValue: unknown
  __facadeGridRawValue: unknown
}>

const pendingRawDisplayCell = Symbol('facade-grid-pending-raw-display-cell')

export type PendingRawDisplayCell = RawDisplayCell & {
  readonly [pendingRawDisplayCell]: true
}

const stagedEditorValues = new WeakMap<
  Readonly<Record<string, unknown>>,
  ReadonlyMap<string, ParsedEditorValue>
>()

export const parsedEditorValue = (
  displayValue: unknown,
  rawValue: unknown
): ParsedEditorValue => ({
  __facadeGridDisplayValue: displayValue,
  __facadeGridRawValue: rawValue
})

export const stageEditorValue = (
  data: Readonly<Record<string, unknown>>,
  field: string,
  value: ParsedEditorValue
): void => {
  const staged = stagedEditorValues.get(data) ?? new Map()
  stagedEditorValues.set(data, new Map([...staged, [field, value]]))
}

export const readStagedEditorValue = (
  data: Readonly<Record<string, unknown>> | undefined,
  field: string
): ParsedEditorValue | undefined =>
  data ? stagedEditorValues.get(data)?.get(field) : undefined

export const clearStagedEditorValue = (
  data: Readonly<Record<string, unknown>>,
  field: string
): void => {
  const staged = stagedEditorValues.get(data)

  if (!staged?.has(field)) {
    return
  }

  const remaining = new Map(
    pipe(
      [...staged],
      filter(([stagedField]) => stagedField !== field)
    )
  )

  if (remaining.size === 0) {
    stagedEditorValues.delete(data)
  } else {
    stagedEditorValues.set(data, remaining)
  }
}

export const isEditorValueCell = (value: unknown): value is EditorValueCell =>
  isPlainObject(value) &&
  (hasProp(value, '_serverValue') || hasProp(value, 'value'))

export const isRawDisplayCell = (value: unknown): value is RawDisplayCell =>
  isPlainObject(value) &&
  hasProp(value, 'value') &&
  hasProp(value, '_serverValue') &&
  !hasProp(value, 'label') &&
  !hasProp(value, 'editorParams')

export const isPendingRawDisplayCell = (
  value: unknown
): value is PendingRawDisplayCell =>
  isRawDisplayCell(value) && Reflect.get(value, pendingRawDisplayCell) === true

export const isStructuredEditorCell = (value: unknown): boolean =>
  isPlainObject(value) &&
  (isRawDisplayCell(value) ||
    hasProp(value, 'editable') ||
    hasProp(value, 'editorParams') ||
    hasProp(value, 'label'))

export const extractEditorValue = (params: EditorValueParams): unknown => {
  const cellData = params.data?.[params.column.getColId()]

  if (!isEditorValueCell(cellData)) {
    return params.value
  }

  return hasProp(cellData, '_serverValue')
    ? cellData._serverValue
    : cellData.value
}

export const extractEditorRawValue = (value: unknown): unknown => {
  if (isParsedEditorValue(value)) {
    return value.__facadeGridRawValue
  }

  if (!isEditorValueCell(value)) {
    return value
  }

  return hasProp(value, '_serverValue') ? value._serverValue : value.value
}

export const extractEditorDisplayValue = (value: unknown): unknown => {
  if (isParsedEditorValue(value)) {
    return value.__facadeGridDisplayValue
  }

  return isEditorValueCell(value) && hasProp(value, 'value')
    ? value.value
    : value
}

const createPendingRawDisplayCell = (
  originalValue: RawDisplayCell,
  displayValue: unknown,
  rawValue: unknown
): PendingRawDisplayCell =>
  // The non-enumerable symbol keeps client-only state out of Phoenix row_data.
  Object.defineProperty(
    {
      ...originalValue,
      value: displayValue,
      _serverValue: rawValue,
      [pendingRawDisplayCell]: true as const
    },
    pendingRawDisplayCell,
    {
      configurable: false,
      enumerable: false,
      value: true,
      writable: false
    }
  )

export const createOptimisticEditorValue = (
  originalValue: unknown,
  transformedValue: unknown,
  formattedDisplayValue: unknown
): unknown => {
  if (isRawDisplayCell(originalValue)) {
    return createPendingRawDisplayCell(
      originalValue,
      isParsedEditorValue(transformedValue)
        ? transformedValue.__facadeGridDisplayValue
        : formattedDisplayValue,
      extractEditorRawValue(transformedValue)
    )
  }

  // Badge, stepper, and raw/display editors reconstruct their own wrappers.
  // Scalar wrappers keep all cell metadata and replace only the canonical value.
  if (
    isEditorValueCell(originalValue) &&
    hasProp(originalValue, 'value') &&
    !hasProp(originalValue, '_serverValue') &&
    !hasProp(originalValue, 'label') &&
    isStructuredEditorCell(originalValue) &&
    !isParsedEditorValue(transformedValue) &&
    !isStructuredEditorCell(transformedValue)
  ) {
    return {
      ...originalValue,
      value: extractEditorRawValue(transformedValue)
    }
  }

  return isParsedEditorValue(transformedValue)
    ? transformedValue.__facadeGridRawValue
    : transformedValue
}

export const setEditorCellValue = (
  data: Record<string, unknown>,
  field: string,
  oldValue: unknown,
  newValue: unknown
): boolean => {
  const currentValue = data[field]

  if (isRawDisplayCell(currentValue) && isParsedEditorValue(newValue)) {
    const changed = !Object.is(
      currentValue._serverValue,
      newValue.__facadeGridRawValue
    )

    if (changed) {
      stageEditorValue(data, field, newValue)
    }

    return changed
  }

  if (
    isStructuredEditorCell(currentValue) &&
    !isStructuredEditorCell(newValue)
  ) {
    return true
  }

  const changed = !Object.is(oldValue, newValue)
  data[field] = newValue
  return changed
}

const optionRawValue = (value: unknown): unknown =>
  isPlainObject(value) && hasProp(value, 'value') ? value.value : value

const optionDisplayValue = (value: unknown): unknown => {
  if (!(isPlainObject(value) && hasProp(value, 'value'))) {
    return value
  }

  if (hasProp(value, 'label') && value.label !== undefined) {
    return value.label
  }

  if (hasProp(value, 'title') && value.title !== undefined) {
    return value.title
  }

  return value.value
}

export const parseRichSelectEditorValue = (
  value: unknown
): ParsedEditorValue | undefined => {
  if (value === undefined) {
    return undefined
  }

  return parsedEditorValue(
    Array.isArray(value)
      ? map(value, optionDisplayValue)
      : optionDisplayValue(value),
    Array.isArray(value) ? map(value, optionRawValue) : optionRawValue(value)
  )
}

export const isParsedEditorValue = (
  value: unknown
): value is ParsedEditorValue =>
  isPlainObject(value) &&
  hasProp(value, '__facadeGridDisplayValue') &&
  hasProp(value, '__facadeGridRawValue')
